import { VisitAnesthesiaFormSurgeryVitalModel } from '@common/models/VisitAnesthesiaFormSurgeryVitalModel';
import { DateTimeUtils } from '@common/utils/datetimeUtils';
import { DateTime } from 'luxon';
import { textEditor } from 'react-data-grid';
export const getFormattedTimeForHeader = (number: number) => {
    const pad = (number: number) => number.toString().padStart(2, '0')
    if (number > 60) {
        const unroundedHours = number / 60
        const hours = Math.floor(number / 60);

        const minutes = Math.round((unroundedHours - hours) * 60);
        minutes.toString().padStart(2, '0')
        return `${hours}:${pad(minutes)}`
    }
    else if (number > -1) {
        return `:${pad(number)}`
    }
    else {
        return 'unknown'
    }
}

export const surgeryDummyColumn = (num: number) => ({
    "visitAnesthesiaFormSurgeryVitalId": 0,
    "visitAnesthesiaFormSurgeryId": 0,
    "fluidRate": '',
    "anesthesiaDepth": '',
    "iso": '',
    "o2Flow": '',
    "hr": '',
    "sP02": '',
    "temperature": '',
    "rr": '',
    "etcO2": '',
    "mm": '',
    "systolic": '',
    "diastolic": '',
    "map": '',
    "interval": num,
    "rowVersion": "",
    "dateCreated": undefined,
    "dateUpdated": undefined
})
export const postSurgeryDummyColumn = (num: number) => ({
    "visitAnesthesiaFormPostSurgeryVitalId": 0,
    "visitAnesthesiaFormPostSurgeryId": 0,
    "temperature": '',
    "hr": '',
    "rr": '',
    "painScore": '',
    "interval": num,
    "rowVersion": "",
    "dateCreated": undefined,
    "dateUpdated": undefined,
})
export const addDummyColumns = (currentNumberOfVitals: number, dummyColumn: (num: number) => any) => {
    const twoHours = 120; // minutes
    const interval = 5; // minutes

    const numberOfColumnsToAdd = (twoHours / interval) - currentNumberOfVitals;
    const dummyColumns = []
    for (let count = 0; count <= numberOfColumnsToAdd; count++) {
        dummyColumns.push(dummyColumn(count + currentNumberOfVitals + 1))
    }
    return dummyColumns;
}


// columns variable: contains all of the properties to customize the grid column.
// columns: [{ key }, {key, name, width, resizable, editor, cellClass, headerCellClass}, {}, ...etc]
export const createColumns = (
    {
        vitals,
        highlightedColumnIndex,
        surgeryHasStopped,
        passedClassName = "",
        timezoneName,
        isBody
    }: {
        vitals: VisitAnesthesiaFormSurgeryVitalModel[],
        highlightedColumnIndex: number,
        surgeryHasStopped: boolean,
        passedClassName: string,
        timezoneName?: string,
        isBody: boolean,
    }
) => {
    const columnArray = vitals.map(({ dateCreated }, index) => {
        const highlightedColumn = index === highlightedColumnIndex && isBody;
        const disabledColumn = index > highlightedColumnIndex;
        const disableHighlightedColumnWhenSurgeryStopped = (surgeryHasStopped && index === highlightedColumnIndex);
        const columnHeader = dateCreated && timezoneName ? DateTimeUtils.getZoneDateAsLocal(dateCreated, timezoneName).toLocaleString(DateTime.TIME_SIMPLE) : ''
        return ({
            key: `${index}`,
            name: columnHeader,
            width: 90,
            resizable: true,
            editor: textEditor,
            /* States supported by both cell class and header class
                1. disable column if surgery is stopped.
                2. disable column if after the highlighted column.
                3. columns can only be created when highlighted which is driven by 2 flags.
                  i. column index
                  ii. highlightEnabled,
                    a. we no longer highlight a column when it has been created
                    b. and the interval to highlight the next column resets
                4. all previous columns to the current column can be edited.

                Note: disabling columns also have javascript logic on whether to trigger modal click event or not.
                onCellClick={(args, event) => {
                    !surgeryHasStopped && checkAndOpenModal(args);
                }
                How the Timer works: located at apps/common/hooks/useGridHighlightTimer.ts
                1. Currently the logic changes the inteval every 5 minutes.
                2. This is changing and instead
                  i. We reset the timer for the following conditions:
                    a. starting the surgery starts the timer.
                    b. saving the highlighted column restarts the timer.
                    c. what does stopping and starting the timer do?
                  ii. We will only ever highlight one column rightward to the last edited column.
            */
            cellClass: (disableHighlightedColumnWhenSurgeryStopped || disabledColumn) // The order of this ternary is important take care when changing.
                ? 'cursor-not-allowed bg-disabled'
                : highlightedColumn
                    ? 'modified-col-cell cursor-pointer' + passedClassName
                    : 'cursor-pointer', // this happens after we edit.
            headerCellClass: (disableHighlightedColumnWhenSurgeryStopped || disabledColumn)
                ? 'react-grid-petco-header cursor-not-allowed bg-disabled'
                : highlightedColumn
                    ? 'react-grid-petco-header modified-header-cell' + passedClassName
                    : 'react-grid-petco-header'
        })
    })
    return columnArray;
} // columns: [{ key }, {}, {}, ...etc] // key is used by the rows.

export function createRows(vitals: VisitAnesthesiaFormSurgeryVitalModel[], surgeryPickKeys: string[]) {
    // shrunkVitals summary:
    // [ // Each vital, which is separated by interval, is contained in a sub array. These items look like the following:
    //   ['1', 'Deep', '2.0', '0.3', '158', '99%', '100.1', '119', '33', '6', '120', '122', '84'],
    //   [],[],[],[],[],[],[],[...etc]
    // ]

    const shrunkVitals = vitals.map((vital) => {
        const shrunkVitals = _.pick(vital, surgeryPickKeys);
        const vitalsAsArray = Object.values(shrunkVitals);
        return vitalsAsArray;
    })
    // [
    //    [fluidRate, anesthesiaDepth, iso, o2Flow, hr, sP02, temperature, rr, etcO2, mm, systolic, diastolic, map],
    //    [fluidRate, anesthesiaDepth, iso, o2Flow, hr, sP02, temperature, rr, etcO2, mm, systolic, diastolic, map],
    //    ...
    // ]
    // needs to be converted to:
    // [
    //    [fluidRate, fluidRate, fluidRate, fluidRate, fluidRate, fluidRate],
    //    [anesthesiaDepth, anesthesiaDepth, anesthesiaDepth, anesthesiaDepth, anesthesiaDepth, anesthesiaDepth, anesthesiaDepth],
    //    ...
    // ]
    const rows = [];
    // Iterate all of the rows and add the cells to the columns, transposing the data.
    for (let rowCount = 0; rowCount < shrunkVitals[0].length; rowCount++) { // There are a finite number of rows.
        const row: any = []
        for (let index = 0; index < vitals.length; index++) { // There are a finite number of rows.
            row.push(shrunkVitals[index][rowCount]) // add the headerKey to the object and value as the cell.
        }
        rows.push(row); // rows are now filled with cells associated with the correct column headers.
    }
    return rows;
    /* rows Summary:
        For react-data-grid rows contain cells of the table.
        Here is an example of what a rows looks like:
        [
            [ '1', '1', ...etc ],
            ['Deep', 'Deep', ...etc],
            ...
        ]
    */
}

export const remapVitalsObjectViaTranspose = (rows: any, vitals: any) => { // currently not used but can be a helpful function in the future.
    // We could in theory edit inline using useForm Hooks. but this is not asked for.
    const transposedRows: any = [];
    for (let count = 0; count <= 24; count++) { transposedRows.push([]) }

    rows.forEach(
        (row: any) => {
            for (let cellCount = 0; cellCount < row.length; cellCount++) {
                const cell = row[cellCount];
                transposedRows[cellCount].push(cell) // creates a 2d array where each row has the different values needed in the payload object.
            }
        }
    )

    const final = transposedRows.map((rowValues: any, index: number) => {
        const [fluidRate, anesthesiaDepth, iso, o2Flow, hr, sP02, temperature, rr, etcO2, mm, systolic, diastolic, map] = rowValues
        const vital = vitals[index];
        return { ...vital, fluidRate, anesthesiaDepth, iso, o2Flow, hr, sP02, temperature, rr, etcO2, mm, systolic, diastolic, map }
    });
    return final;

    // note, "the below content" is not useful because gridCells would not be part of useFormHooks. but leaving as it might be useful for the modal.
    // const [fluidRate, anesthesiaDepth, iso, o2Flow, hr, sP02, temperature, rr, etcO2, mm, systolic, diastolic, map] = gridCells[index]
    // return { ...vital, fluidRate, anesthesiaDepth, iso, o2Flow, hr, sP02, temperature, rr, etcO2, mm, systolic, diastolic, map }
}
